import { useEffect, useMemo, useState } from "react";
import { isFederalStateChoice, NO_FEDERAL_STATE, type FederalStateChoice } from "../../../constants/FederalStates";
import type { BookFilters } from "../../../helpers/BookAvailability";
import {
    buildCatalogLanguages,
    findCatalogVolume,
    hasIsbn,
    searchCatalogBooks,
    type CatalogBook,
} from "../../../helpers/BookCatalog";
import type { BookCatalogVolume } from "../../../networking/bookCatalog";

export const FEDERAL_STATE_STORAGE_KEY = "bookSelection.federalState";

export type BookSelectionStep = 1 | 2 | 3 | 4;

/** "unavailable": the catalog has the ISBN, but its book is blocked for this activity. */
export type IsbnLookupResult = "picked" | "unavailable" | "not-found";

const readSavedFederalState = (): FederalStateChoice | null => {
    const saved = localStorage.getItem(FEDERAL_STATE_STORAGE_KEY);
    return isFederalStateChoice(saved) ? saved : null;
};

interface BookSelectionStepsOptions {
    /** Undefined while loading. */
    catalog?: Array<BookCatalogVolume>;
    filters: BookFilters;
    /** The volume of an earlier selection (back navigation, book deep link), reopened at the volume step. */
    initialArticleId?: string;
}

/** State → language → book → volume. Changing a step clears the ones after it, since they depend on it. */
export const useBookSelectionSteps = ({ catalog, filters, initialArticleId }: BookSelectionStepsOptions) => {
    const [savedFederalState] = useState(readSavedFederalState);
    const [step, setStep] = useState<BookSelectionStep>(savedFederalState ? 2 : 1);
    const [federalState, setFederalState] = useState<FederalStateChoice | null>(savedFederalState);
    const [isFederalStateRemembered, setIsFederalStateRemembered] = useState(!!savedFederalState);
    const [shouldRememberFederalState, setShouldRememberFederalState] = useState(true);
    const [subjectId, setSubjectId] = useState<string>();
    const [book, setBook] = useState<CatalogBook>();
    const [volume, setVolume] = useState<BookCatalogVolume>();
    const [searchQuery, setSearchQuery] = useState("");

    const languages = useMemo(
        () =>
            catalog
                ? buildCatalogLanguages({ volumes: catalog, state: federalState ?? NO_FEDERAL_STATE, filters })
                : [],
        [catalog, federalState, filters]
    );
    const language = languages.find((candidate) => candidate.subjectId === subjectId);
    const books = language?.books ?? [];
    // Across all languages, so a teacher can find a book before picking (or without knowing) its language.
    const searchResults = useMemo(
        () =>
            searchQuery.trim()
                ? searchCatalogBooks(
                      languages.flatMap((candidate) => candidate.books),
                      searchQuery
                  )
                : [],
        [languages, searchQuery]
    );

    useEffect(() => {
        if (!catalog || !initialArticleId) {
            return;
        }

        // Looked up regardless of the state: it's the teacher's own earlier choice, so all its volumes stay offered.
        const earlierSelection = findCatalogVolume(
            buildCatalogLanguages({ volumes: catalog, state: NO_FEDERAL_STATE, filters }),
            (volume) => volume.articleId === initialArticleId
        );
        if (!earlierSelection) {
            return;
        }

        setFederalState((current) => current ?? NO_FEDERAL_STATE);
        setSubjectId(earlierSelection.language.subjectId);
        setBook(earlierSelection.book);
        setVolume(earlierSelection.volume);
        setStep(4);
        // Once, when the catalog arrives: afterwards the selection is this flow's own.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog]);

    const isStepReachable = (target: BookSelectionStep) =>
        target === 1 || (target === 2 && !!federalState) || (target === 3 && !!language) || (target === 4 && !!book);

    const goToStep = (target: BookSelectionStep) => {
        if (isStepReachable(target)) {
            setStep(target);
        }
    };

    const pickFederalState = (choice: FederalStateChoice) => {
        if (shouldRememberFederalState) {
            localStorage.setItem(FEDERAL_STATE_STORAGE_KEY, choice);
        } else {
            localStorage.removeItem(FEDERAL_STATE_STORAGE_KEY);
        }

        if (choice !== federalState) {
            setSubjectId(undefined);
            setBook(undefined);
            setVolume(undefined);
        }
        setFederalState(choice);
        setIsFederalStateRemembered(false);
        setStep(2);
    };

    /** Applies at once, so unticking also forgets a state saved earlier in this flow. */
    const changeShouldRememberFederalState = (shouldRemember: boolean) => {
        setShouldRememberFederalState(shouldRemember);
        if (!shouldRemember) {
            localStorage.removeItem(FEDERAL_STATE_STORAGE_KEY);
        } else if (federalState) {
            localStorage.setItem(FEDERAL_STATE_STORAGE_KEY, federalState);
        }
    };

    const pickLanguage = (pickedSubjectId: string) => {
        if (pickedSubjectId !== subjectId) {
            setBook(undefined);
            setVolume(undefined);
        }
        setSubjectId(pickedSubjectId);
        setStep(3);
    };

    /** From step 3 or from the search, which may hold a book of another language than the one picked. */
    const pickBook = (pickedBook: CatalogBook) => {
        // A reopened book carries all its volumes, the same book in step 3 only the state's.
        const keepsVolume =
            !!volume && pickedBook.volumes.some((candidate) => candidate.articleId === volume.articleId);
        if (!keepsVolume) {
            const [onlyVolume, ...otherVolumes] = pickedBook.volumes;
            setVolume(otherVolumes.length === 0 && !onlyVolume.isComingSoon ? onlyVolume : undefined);
        }
        setFederalState((current) => current ?? NO_FEDERAL_STATE);
        setSubjectId(pickedBook.subjectId);
        setBook(pickedBook);
        setSearchQuery("");
        setStep(4);
    };

    /** Jumps straight to the volume step of the volume with this ISBN. */
    const pickVolumeByIsbn = (isbn: string): IsbnLookupResult => {
        if (!catalog) {
            return "not-found";
        }

        // Looked up regardless of the state, like an earlier selection: the ISBN names the volume exactly.
        const match = findCatalogVolume(
            buildCatalogLanguages({ volumes: catalog, state: NO_FEDERAL_STATE, filters }),
            (volume) => hasIsbn(volume, isbn)
        );
        if (!match) {
            return catalog.some((volume) => hasIsbn(volume, isbn)) ? "unavailable" : "not-found";
        }

        setFederalState((current) => current ?? NO_FEDERAL_STATE);
        setSubjectId(match.language.subjectId);
        setBook(match.book);
        setVolume(match.volume.isComingSoon ? undefined : match.volume);
        setStep(4);
        return "picked";
    };

    const pickVolume = (pickedVolume: BookCatalogVolume) => {
        if (!pickedVolume.isComingSoon) {
            setVolume(pickedVolume);
        }
    };

    return {
        step,
        goToStep,
        isStepReachable,
        federalState,
        isFederalStateRemembered,
        shouldRememberFederalState,
        setShouldRememberFederalState: changeShouldRememberFederalState,
        pickFederalState,
        languages,
        language,
        pickLanguage,
        books,
        searchQuery,
        setSearchQuery,
        searchResults,
        book,
        pickBook,
        volume,
        pickVolume,
        pickVolumeByIsbn,
    };
};
